import type { CSSProperties } from 'react';
import type { DateRange, DayPickerProps } from 'react-day-picker';
import { DateLib } from 'react-day-picker';

export const visuallyHiddenStyles: CSSProperties = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
  wordWrap: 'normal',
};

export const normalizeMonth = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), 1);

export const getMonthKey = (date: Date): number =>
  date.getFullYear() * 12 + date.getMonth();

export const areDatesEqual = (first?: Date, second?: Date): boolean => {
  if (!first && !second) {
    return true;
  }
  if (!first || !second) {
    return false;
  }
  return first.getTime() === second.getTime();
};

export const areRangesEqual = (
  first?: DateRange,
  second?: DateRange,
): boolean => {
  return (
    areDatesEqual(first?.from, second?.from) &&
    areDatesEqual(first?.to, second?.to)
  );
};

export const rangeFromProps = (
  start?: Date | null,
  end?: Date | null,
): DateRange | undefined => {
  const from = start ?? undefined;
  const to = end ?? undefined;

  if (!from && !to) {
    return undefined;
  }

  return { from, to };
};

export const parseTranslationList = (
  value: unknown,
  fallback: readonly string[],
): string[] => {
  if (typeof value !== 'string') {
    return [...fallback];
  }

  const parts = value
    .split('|')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length !== fallback.length) {
    return [...fallback];
  }

  return parts;
};

export const createMonthBoundaryModifierClassNames = () => ({
  firstDayOfMonth: ['first-day-of-month'].filter(Boolean).join(' '),
  lastDayOfMonth: ['last-day-of-month'].filter(Boolean).join(' '),
});

export const isLastDayOfMonth = (date: Date): boolean => {
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return lastDay.getDate() === date.getDate();
};

export const monthValueFromDate = (dateLib: DateLib, date: Date): number => {
  const year = dateLib.getYear(date);
  const month = dateLib.getMonth(date);
  return year * 12 + month;
};

export const dateFromMonthValue = (dateLib: DateLib, value: number): Date => {
  const year = Math.floor(value / 12);
  const month = value % 12;
  return dateLib.startOfMonth(dateLib.newDate(year, month, 1));
};

export const getNavigationRange = (
  props: DayPickerProps,
  dateLib: DateLib,
): [Date | undefined, Date | undefined] => {
  let startMonth = props.startMonth ?? props.fromMonth;
  let endMonth = props.endMonth ?? props.toMonth;

  if (!startMonth && props.fromYear !== undefined) {
    startMonth = dateLib.newDate(props.fromYear, 0, 1);
  }

  if (!endMonth && props.toYear !== undefined) {
    endMonth = dateLib.newDate(props.toYear, 11, 31);
  }

  const hasYearDropdown =
    props.captionLayout === 'dropdown' ||
    props.captionLayout === 'dropdown-years';

  if (startMonth) {
    startMonth = dateLib.startOfMonth(startMonth);
  } else if (hasYearDropdown) {
    const base = props.today ?? dateLib.today();
    startMonth = dateLib.startOfYear(dateLib.addYears(base, -100));
  }

  if (endMonth) {
    endMonth = dateLib.endOfMonth(endMonth);
  } else if (hasYearDropdown) {
    const base = props.today ?? dateLib.today();
    const monthsToAdd = Math.max(0, (props.numberOfMonths ?? 1) - 1);
    const futureMonthStart = dateLib.startOfMonth(dateLib.addYears(base, 10));
    const extendedEnd = dateLib.addMonths(futureMonthStart, monthsToAdd);
    endMonth = dateLib.endOfMonth(extendedEnd);
  }

  return [
    startMonth ? dateLib.startOfDay(startMonth) : undefined,
    endMonth ? dateLib.startOfDay(endMonth) : undefined,
  ];
};

export const buildMonthYearOptions = (
  dateLib: DateLib,
  start: Date | undefined,
  end: Date | undefined,
  referenceMonth: Date,
) => {
  const referenceStart = dateLib.startOfMonth(referenceMonth);
  const startBound = start ? dateLib.startOfMonth(start) : undefined;
  const endBound = end ? dateLib.startOfMonth(end) : undefined;

  const from = startBound
    ? startBound
    : endBound
      ? dateLib.startOfMonth(dateLib.addYears(endBound, -20))
      : dateLib.startOfMonth(dateLib.addYears(referenceStart, -10));

  const to = endBound
    ? endBound
    : startBound
      ? dateLib.startOfMonth(dateLib.addYears(startBound, 20))
      : dateLib.startOfMonth(dateLib.addYears(referenceStart, 10));

  if (!from || !to) {
    return [];
  }

  const startValue = startBound
    ? monthValueFromDate(dateLib, startBound)
    : undefined;
  const endValue = endBound ? monthValueFromDate(dateLib, endBound) : undefined;

  const options: Array<{
    value: number;
    label: string;
    disabled: boolean;
  }> = [];

  let cursor = dateLib.startOfMonth(from);
  while (dateLib.isBefore(cursor, to) || dateLib.isSameMonth(cursor, to)) {
    const value = monthValueFromDate(dateLib, cursor);
    const disabled =
      (startValue !== undefined && value < startValue) ||
      (endValue !== undefined && value > endValue);

    options.push({
      value,
      label: dateLib.formatMonthYear(cursor),
      disabled,
    });
    cursor = dateLib.addMonths(cursor, 1);
  }

  return options;
};
